import { CurrentIdentitySchema } from '@repo/auth/contracts';
import type { FlagsIdentityState } from '@repo/flags/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { getCurrentIdentity } from '@/api/current-identity.api';
import type {
  TemplateBuilderAuth,
  TemplateBuilderConfig,
  TemplateBuilderTelemetry,
} from '@/lib/config/template-builder-config';

/** Resolves the authenticated identity used for browser feature-flag targeting. */
export function useFlagIdentity(config: TemplateBuilderConfig): FlagsIdentityState {
  const { auth } = config;
  const latestTrackError = useRef(config.telemetry.trackError);
  latestTrackError.current = config.telemetry.trackError;
  const reportedFailures = useRef(new WeakSet<TemplateBuilderAuth>());
  const suppliedIdentityResult = useMemo(
    () =>
      auth.identity === undefined ? undefined : CurrentIdentitySchema.safeParse(auth.identity),
    [auth],
  );
  const [resolution, setResolution] = useState<IdentityResolution>({
    auth,
    identityState: { status: 'loading' },
  });

  useEffect(() => {
    if (suppliedIdentityResult !== undefined) {
      if (!suppliedIdentityResult.success) {
        reportIdentityFailure({
          auth,
          error: suppliedIdentityResult.error,
          latestTrackError,
          reportedFailures: reportedFailures.current,
        });
      }
      return;
    }

    let cancelled = false;
    getCurrentIdentity().then(
      (identity) => {
        if (!cancelled) {
          setResolution({ auth, identityState: { status: 'ready', identity } });
        }
      },
      (error: unknown) => {
        if (!cancelled) {
          reportIdentityFailure({
            auth,
            error,
            latestTrackError,
            reportedFailures: reportedFailures.current,
          });
          setResolution({ auth, identityState: { status: 'failed' } });
        }
      },
    );

    return () => {
      cancelled = true;
    };
  }, [auth, suppliedIdentityResult]);

  if (suppliedIdentityResult !== undefined) {
    return suppliedIdentityResult.success
      ? { status: 'ready', identity: suppliedIdentityResult.data }
      : { status: 'failed' };
  }

  return resolution.auth === auth ? resolution.identityState : { status: 'loading' };
}

/** Sends one terminal identity-resolution error for each auth object. */
function reportIdentityFailure({
  auth,
  error,
  latestTrackError,
  reportedFailures,
}: ReportIdentityFailureParams): void {
  if (reportedFailures.has(auth)) return;

  reportedFailures.add(auth);
  latestTrackError.current(error, { operation: 'resolve-feature-flag-identity' });
}

interface IdentityResolution {
  auth: TemplateBuilderAuth;
  identityState: FlagsIdentityState;
}

interface ReportIdentityFailureParams {
  auth: TemplateBuilderAuth;
  error: unknown;
  latestTrackError: { current: TemplateBuilderTelemetry['trackError'] };
  reportedFailures: WeakSet<TemplateBuilderAuth>;
}
